import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FavoriteButton } from "@/components/FavoriteButton";
import { destinations, CATEGORIES } from "@/data/destinations";
import api from "@/lib/api";
import { MapPin, Star, ArrowRight, Search } from "lucide-react";

export default function PlanTrip() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");
  const [customDests, setCustomDests] = useState([]);

  useEffect(() => {
    api.get("/destinations").then((r) => setCustomDests(r.data)).catch(() => {});
  }, []);

  const allDestinations = useMemo(
    () => [...destinations, ...customDests],
    [customDests]
  );

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return allDestinations.filter((d) => {
      if (category !== "All" && d.category !== category) return false;
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        d.location.toLowerCase().includes(q) ||
        d.description.toLowerCase().includes(q)
      );
    });
  }, [allDestinations, category, query]);

  const planFor = (place) => {
    navigate(
      `/booking?interest=${encodeURIComponent(place.category)}&place=${encodeURIComponent(place.name)}`
    );
  };

  return (
    <div className="min-h-screen bg-background font-sans flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="relative pt-32 pb-12 bg-primary-foreground overflow-hidden">
          <div className="absolute top-0 right-0 w-[28rem] h-[28rem] bg-secondary/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-[28rem] h-[28rem] bg-primary/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
            <Badge variant="outline" className="mb-4 border-primary/20 text-primary px-4 py-1">
              Plan Your Sri Lanka Trip
            </Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-bold text-primary mb-4">
              Every destination, <span className="text-secondary">one place</span>
            </h1>
            <p className="text-muted-foreground text-lg">
              Browse {allDestinations.length} curated places — beaches, ancient cities, safaris and misty hill country — then build your own itinerary.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="py-8 bg-background sticky top-16 z-30 border-b border-primary/5 backdrop-blur-sm bg-background/80">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search places, e.g. Mirissa, Yala, Sigiriya…"
                  data-testid="plantrip-search"
                  className="pl-10 bg-white"
                />
              </div>
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map((c) => (
                  <Button
                    key={c}
                    size="sm"
                    variant={category === c ? "default" : "outline"}
                    onClick={() => setCategory(c)}
                    data-testid={`plantrip-filter-${c.toLowerCase()}`}
                    className={`rounded-full ${
                      category === c
                        ? "bg-primary text-white"
                        : "border-primary/20 hover:bg-primary/5"
                    }`}
                  >
                    {c}
                  </Button>
                ))}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Showing {filtered.length} of {allDestinations.length} destinations
            </p>
          </div>
        </section>

        {/* Grid */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            {filtered.length === 0 ? (
              <p className="text-center text-muted-foreground py-20">
                No destinations match your search.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="plantrip-grid">
                {filtered.map((d) => (
                  <Card
                    key={d.place_id}
                    className="group overflow-hidden border-none shadow-md hover:shadow-2xl transition-all duration-300 bg-white"
                  >
                    <div className="relative h-52 overflow-hidden">
                      <FavoriteButton
                        place={{
                          place_id: d.place_id,
                          name: d.name,
                          image: d.image,
                          location: d.location,
                        }}
                      />
                      <img
                        src={d.image}
                        alt={d.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute top-4 right-16 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1 text-xs font-medium text-primary shadow-sm">
                        <Star className="w-3 h-3 fill-secondary text-secondary" />
                        {d.rating}
                      </div>
                      <div className="absolute top-4 left-4">
                        <Badge className="bg-primary/90 hover:bg-primary text-white border-none text-xs">
                          {d.category}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 text-muted-foreground text-xs mb-2">
                        <MapPin className="w-3 h-3 text-secondary" />
                        {d.location}
                      </div>
                      <h3 className="text-xl font-serif font-bold text-primary mb-2 group-hover:text-secondary transition-colors">
                        {d.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                        {d.description}
                      </p>
                      <Button
                        size="sm"
                        onClick={() => planFor(d)}
                        data-testid={`plantrip-cta-${d.place_id}`}
                        className="w-full bg-primary text-white hover:bg-primary/90 rounded-full"
                      >
                        Plan a trip here
                        <ArrowRight className="w-3 h-3 ml-2" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-primary text-white">
          <div className="container mx-auto px-4 text-center max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
              Can't decide? Let us craft it for you.
            </h2>
            <p className="text-white/80 mb-8">
              Share your interests and we'll design a personalised Sri Lanka itinerary.
            </p>
            <Link to="/booking">
              <Button
                size="lg"
                className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-serif rounded-full px-8 h-12"
              >
                Start Your Booking
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
